import React from 'react';
import { motion } from 'framer-motion';
import FloatingBackground from '../../Common/FloatingBackground/FloatingBackground';
import './Impact.css';

const Impact: React.FC = () => {

  return (
    <motion.section 
      className='impact modern-impact'
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <FloatingBackground />
      <div className='container'>
        <div className='section-header'>
          <h2 className='section-title modern-title'>
            Real Impact in Clinical Trials{' '}
            <span className='gradient-text'>Digital Solutions</span>
          </h2>
          <p className='section-subtitle'>
            Measurable outcomes powered by AI and automation
          </p>
        </div>
        <div className='impact-dashboard'>
          <div className='impact-stat modern-stat'>
            <div className='stat-indicator'>
              <div className='stat-number'>60%</div>
              <div className='stat-icon'>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 3V21H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 9L12 6L16 10L20 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <div className='stat-content'>
              <h3>Fewer Protocol Deviations</h3>
              <p>
                Via <strong>system validation</strong> and <strong>compliance frameworks</strong>
              </p>
            </div>
          </div>
          <div className='impact-stat modern-stat'>
            <div className='stat-indicator'>
              <div className='stat-number'>80%</div>
              <div className='stat-icon'>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <div className='stat-content'>
              <h3>Faster Timelines</h3>
              <p>
                Integrated workflows and <strong>live</strong> <strong>communication</strong>
              </p>
            </div>
          </div>
          <div className='impact-stat modern-stat'>
            <div className='stat-indicator'>
              <div className='stat-number'>95%</div>
              <div className='stat-icon'>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 19C9 19.5304 9.21071 20.0391 9.58579 20.4142C9.96086 20.7893 10.4696 21 11 21C11.5304 21 12.0391 20.7893 12.4142 20.4142C12.7893 20.0391 13 19.5304 13 19V5C13 4.46957 12.7893 3.96086 12.4142 3.58579C12.0391 3.21071 11.5304 3 11 3C10.4696 3 9.96086 3.21071 9.58579 3.58579C9.21071 3.96086 9 4.46957 9 5V19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 9C5 9.53043 5.21071 10.0391 5.58579 10.4142C5.96086 10.7893 6.46957 11 7 11C7.53043 11 8.03914 10.7893 8.41421 10.4142C8.78929 10.0391 9 9.53043 9 9V5C9 4.46957 8.78929 3.96086 8.41421 3.58579C8.03914 3.21071 7.53043 3 7 3C6.46957 3 5.96086 3.21071 5.58579 3.58579C5.21071 3.96086 5 4.46957 5 5V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M13 12C13 12.5304 13.2107 13.0391 13.5858 13.4142C13.9609 13.7893 14.4696 14 15 14C15.5304 14 16.0391 13.7893 16.4142 13.4142C16.7893 13.0391 17 12.5304 17 12V5C17 4.46957 16.7893 3.96086 16.4142 3.58579C16.0391 3.21071 15.5304 3 15 3C14.4696 3 13.9609 3.21071 13.5858 3.58579C13.2107 3.96086 13 4.46957 13 5V12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M19 15C19 15.5304 19.2107 16.0391 19.5858 16.4142C19.9609 16.7893 20.4696 17 21 17C21.5304 17 22.0391 16.7893 22.4142 16.4142C22.7893 16.0391 23 15.5304 23 15V5C23 4.46957 22.7893 3.96086 22.4142 3.58579C22.0391 3.21071 21.5304 3 21 3C20.4696 3 19.9609 3.21071 19.5858 3.58579C19.2107 3.96086 19 4.46957 19 5V15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <div className='stat-content'>
              <h3>Faster Data Processing</h3>
              <p>
                Instant insights with <strong>live monitoring</strong> and <strong>data visualization</strong>
              </p>
            </div>
          </div>
          <div className='impact-stat modern-stat'>
            <div className='stat-indicator'>
              <div className='stat-number'>98%</div>
              <div className='stat-icon'>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20 8V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M23 11H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <div className='stat-content'>
              <h3>Patient Compliance</h3>
              <p>
                Mobile-first <strong>interfaces</strong> and <strong>automated</strong> reminders
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default Impact;
